import { kitopiColors } from '@kitopi-com/customer-auth';
import { type CustomerProfileResponse, useCustomerProfileApi } from '@kitopi-com/customer-profile';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation } from 'react-router';
import ClipLoader from 'react-spinners/ClipLoader';
import { useTimeout } from 'usehooks-ts';
import { getSearchParams } from 'utils/getSearchParams';
import { SuccessContainer } from './SuccessContainerWrapper.styles';

const reloadCaptivePortal = (location: Location, stop: number) => {
  if (location.search) {
    const params = getSearchParams(location.search);
    const reloadNumber = Number(params?.reload_number);
    if (reloadNumber && reloadNumber < stop) {
      window.location.href = `${location.pathname}?reload_number=${reloadNumber + 1}&siteName=${params.siteName}`;
    }
  }
};

interface SuccessContainerWrapperProps {
  titleFontSize: string;
}

export const SuccessContainerWrapper = ({ titleFontSize }: SuccessContainerWrapperProps) => {
  const customerProfileApi = useCustomerProfileApi();
  const [profile, setProfile] = useState<CustomerProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('wifi');
  const location = useLocation();

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const profileData = await customerProfileApi.getCustomerProfile();
      setProfile(profileData);
    } catch (error) {
      console.error('Failed to fetch customer profile:', error);
      setError(t('profileLoadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    reloadCaptivePortal(location, 1);
    fetchProfile();
  }, []);

  useTimeout(() => {
    reloadCaptivePortal(location, 3);
  }, 8000);

  if (isLoading) {
    return (
      <SuccessContainer titleFontSize={titleFontSize}>
        <ClipLoader size={40} color={kitopiColors.kitopiWhite} />
      </SuccessContainer>
    );
  }

  if (error) {
    return (
      <SuccessContainer titleFontSize={titleFontSize}>
        <ErrorDisplay message={error} />
      </SuccessContainer>
    );
  }

  const displayName = profile?.identity?.name || 'User';

  return (
    <SuccessContainer titleFontSize={titleFontSize}>
      <h2>{t('welcomeUser', { name: displayName })}</h2>
      <p>{t('youAreNowConnected')}</p>
    </SuccessContainer>
  );
};
